import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';
import SvgMenuRingilSmall from '@components/icons/menu/MenuRingilSmall.tsx';
import SvgMenuRingil from '@app/icons/menu/MenuRingil.tsx';
import SvgArrowLeftFromLine from '@icons/arrow-left-from-line.svg?react';
import SvgArrowRightToLine from '@icons/arrow-right-to-line.svg?react';
import SvgMenuAuto from '@icons/menu-auto.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';
import { SidebarMode } from '@app/layout/useSidebarState.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';

interface ComponentProps {
  /** Rendering as the icon rail. */
  smallMenu: boolean;
  /** The mode currently chosen; its segment is the highlighted one. */
  mode: SidebarMode;
  onSelectMode: (mode: SidebarMode) => void;
}

const MenuHeader: React.FC<ComponentProps> = ({ smallMenu, mode, onSelectMode }) => {
  const { tr } = useTranslationLgs();

  // The control resting state shows only the mode that is in effect, as a plain glyph; the other two
  // modes appear only once it is opened, so the header stays quiet until the user asks to change it.
  const [open, setOpen] = useState(false);
  const switchRef = useRef<HTMLDivElement>(null);

  // Ordered as in the design: collapse on the left, auto in the middle, expand on the right.
  const modes: { mode: SidebarMode; Icon: React.FC<React.SVGProps<SVGSVGElement>>; label: string }[] = [
    { mode: 'collapsed', Icon: SvgArrowLeftFromLine, label: tr('Layout.menuModeCollapse', 'Collapse the menu') },
    { mode: 'auto', Icon: SvgMenuAuto, label: tr('Layout.menuModeAuto', 'Size the menu automatically') },
    { mode: 'expanded', Icon: SvgArrowRightToLine, label: tr('Layout.menuModeExpand', 'Expand the menu') },
  ];

  // Opening is a transient choice: anything that is not a pick inside the group puts it back to
  // showing just the current mode.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!switchRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // The rail hides the control altogether, so it must not stay open behind the collapse.
  useEffect(() => {
    if (smallMenu) setOpen(false);
  }, [smallMenu]);

  return (
    // No horizontal padding here: the collapsed mark is positioned at the same offset as the rows'
    // icons (pl-7 + a 24px glyph = centred in the 80px rail), which padding would shift out of line.
    <div className={'relative py-4 bg-base-quaternary flex items-center h-[72px] shrink-0 overflow-hidden'}>
      {/* Both marks stay mounted and cross-fade, so widening the panel does not swap the logo abruptly. */}
      <div className={'relative w-full h-full flex items-center'}>
        <span
          className={twMerge(clsx('absolute left-7 transition-opacity duration-150 ease-in-out motion-reduce:transition-none', smallMenu ? 'opacity-100' : 'opacity-0'))}
          aria-hidden={!smallMenu}
        >
          <SvgMenuRingilSmall />
        </span>
        <span
          className={twMerge(clsx('absolute left-4 transition-opacity duration-150 ease-in-out motion-reduce:transition-none', smallMenu ? 'opacity-0' : 'opacity-100'))}
          aria-hidden={smallMenu}
        >
          <SvgMenuRingil />
        </span>
      </div>
      <div
        ref={switchRef}
        className={clsx(
          // right-[9px] = the rows' pr-4 (16px) minus the last segment's own 6px padding and the
          // group's 1px border, so the rightmost glyph lines up with the menu rows' right edge.
          'absolute right-[9px] top-1/2 -translate-y-1/2 flex items-center overflow-hidden rounded border border-solid',
          'transition-[opacity,background-color,border-color] duration-200 ease-in-out motion-reduce:transition-none',
          // Closed the group is invisible chrome around a single glyph; open it becomes the segmented control.
          open ? 'border-content-brandMedium bg-base-secondary' : 'border-transparent bg-transparent',
          // The rail has no room for the control; it reappears as soon as the panel widens.
          smallMenu ? 'opacity-0 pointer-events-none' : 'opacity-100',
        )}
        data-testid={'menuModeSwitch'}
      >
        {modes.map(m => {
          const selected = m.mode === mode;
          // Closed, the two other modes collapse to nothing so the control animates open sideways
          // instead of popping in, and the current mode keeps its place on the right.
          const hidden = !open && !selected;
          return (
            <RingilButton
              key={m.mode}
              mode={'link'}
              // The colour sits on the glyph itself (the assets paint with currentColor), so the
              // selected segment stays content-primary through the button's own hover colour too.
              icon={<m.Icon className={clsx('h-4 w-4', open && selected && 'text-content-primary')} />}
              // Closed the glyph is a picker opener, not the action its arrow depicts, so it says so.
              tooltip={hidden ? undefined : open ? m.label : tr('Layout.menuModeSwitch', 'Menu size')}
              onClick={() => {
                if (!open) {
                  setOpen(true);
                  return;
                }
                onSelectMode(m.mode);
                setOpen(false);
              }}
              // Only the selected segment is filled, and only while the group is open — closed there
              // is nothing to distinguish it from.
              className={clsx(
                'h-auto rounded-none py-1 transition-[width,padding,opacity,background-color] duration-200 ease-in-out motion-reduce:transition-none',
                hidden ? 'w-0 px-0 opacity-0 pointer-events-none' : 'px-1.5 opacity-100',
                open && (selected ? 'bg-content-brandMedium' : 'hover:bg-base-quaternary'),
              )}
              dataTestId={`menuMode-${m.mode}`}
            />
          );
        })}
      </div>
    </div>
  );
};

export default MenuHeader;
